import { FC, useEffect, useRef, useState } from "react";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";
import { Button } from "@src/lib/ui/Button";
import XIcon from "@src/lib/icons/12px/x";
import { waitForConfetti } from "@src/lib/utils/confetti";
import { cn } from "@src/lib/utils/cn";
import ReleaseLetter, { RELEASE_PAPER_CLASSNAME } from "./ReleaseLetter";
import { celebrateAroundCard } from "./releaseConfetti";
import { ReleaseHeadlineRelease } from "./releases";

// Gives the card's entrance animation a beat to land before the celebration.
const CELEBRATION_DELAY_MS = 900;

// Inline announcement at the top of the homepage: the letter, docked.
const ReleaseHeadlineCard: FC<{
  release: ReleaseHeadlineRelease;
  isPreview: boolean;
  // False when the user already celebrated this release another way, e.g. by
  // opening its envelope.
  celebrate: boolean;
  // Takes focus when it replaces the envelope dialog, so keyboard users land
  // on the card instead of the page body.
  focusOnMount?: boolean;
}> = ({ release, isPreview, celebrate, focusOnMount = false }) => {
  const cardRef = useRef<HTMLElement>(null);
  const [dismissed, setDismissed] = useBooleanLocalStorage(
    `dismissed_release_headline_${release.slug}`,
    false,
  );
  const [celebrated, setCelebrated] = useBooleanLocalStorage(
    `celebrated_release_headline_${release.slug}`,
    false,
  );
  // Previews ignore stored dismissal so they always render, and dismissing a
  // preview only hides it until the next load.
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const hidden = isPreview ? previewDismissed : dismissed;
  const dismiss = () =>
    isPreview ? setPreviewDismissed(true) : setDismissed(true);

  // Confetti greets each admin once per release, the first time they see it.
  useEffect(() => {
    if (hidden || !celebrate || (celebrated && !isPreview)) return;
    let cancelled = false;
    let cancelCelebration = () => undefined as void;
    const timer = setTimeout(async () => {
      if (!(await waitForConfetti()) || cancelled || !cardRef.current) return;
      cancelCelebration = celebrateAroundCard(
        cardRef.current.getBoundingClientRect(),
      );
      if (!isPreview) setCelebrated(true);
    }, CELEBRATION_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      cancelCelebration();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, []);

  useEffect(() => {
    if (focusOnMount) cardRef.current?.focus({ preventScroll: true });
  }, [focusOnMount]);

  if (hidden) return null;

  const headingId = `release-headline-${release.slug}`;

  return (
    <section
      ref={cardRef}
      aria-labelledby={headingId}
      data-testid="release-headline"
      tabIndex={focusOnMount ? -1 : undefined}
      className={cn(
        // The letter's layout, docked: the headline and features beside the
        // preview, at a fixed height so the homepage below doesn't jump as
        // the tour moves through the features.
        "relative isolate overflow-hidden rounded-xl border border-border-primary outline-none motion-safe:duration-500 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 md:h-118",
        RELEASE_PAPER_CLASSNAME,
      )}
    >
      <Button
        variant="outline"
        size="icon-md"
        onClick={dismiss}
        aria-label="Dismiss banner"
        className="absolute top-4 right-4 z-10"
      >
        <XIcon />
      </Button>
      <ReleaseLetter release={release} headingId={headingId} layout="card" />
    </section>
  );
};

export default ReleaseHeadlineCard;
